import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  FileText, ChevronUp, ChevronDown, Send, RefreshCw, CheckCircle
} from 'lucide-react';

/**
 * "Commandes DCE" tab content. Lists all DCE orders, allows admin to expand
 * each row, deliver paid-but-not-delivered orders, and see delivery status.
 */
export default function DceOrdersTab({
  dceOrders,
  expandedOrder,
  setExpandedOrder,
  deliveryUrl,
  setDeliveryUrl,
  delivering,
  deliverOrder,
  getStatusBadge,
  formatDate,
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Commandes DCE ({dceOrders.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {dceOrders.length === 0 ? (
          <p className="text-center text-slate-500 py-8">Aucune commande DCE</p>
        ) : (
          <div className="space-y-4">
            {dceOrders.map((order) => (
              <div key={order.id} className="border rounded-lg overflow-hidden">
                <div
                  className="p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 flex items-center justify-between"
                  onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                >
                  <div className="flex items-center gap-4">
                    {getStatusBadge(order.status, order.payment_status)}
                    <div>
                      <p className="font-medium text-slate-900">{order.pack_name}</p>
                      <p className="text-sm text-slate-500">{order.tender_title?.substring(0, 50)}...</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-green-600">{order.amount} €</p>
                      <p className="text-xs text-slate-500">{formatDate(order.created_at)}</p>
                    </div>
                    {expandedOrder === order.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>

                {expandedOrder === order.id && (
                  <div className="p-4 border-t bg-white">
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-slate-500">Client</p>
                        <p className="font-medium">{order.user_email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Email de livraison</p>
                        <p className="font-medium">{order.delivery_email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Référence marché</p>
                        <p className="font-medium">{order.tender_reference || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">N° commande</p>
                        <p className="font-medium text-xs">{order.id}</p>
                      </div>
                    </div>

                    {order.payment_status === 'paid' && order.status !== 'delivered' && (
                      <div className="mt-4 p-4 bg-amber-50 rounded-lg">
                        <p className="font-medium text-amber-800 mb-2">Livrer cette commande</p>
                        <div className="flex gap-2">
                          <input
                            type="url"
                            placeholder="URL de téléchargement du DCE"
                            value={deliveryUrl}
                            onChange={(e) => setDeliveryUrl(e.target.value)}
                            className="flex-1 px-3 py-2 border rounded-lg text-sm"
                          />
                          <Button
                            onClick={() => deliverOrder(order.id)}
                            disabled={delivering === order.id}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {delivering === order.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Livrer
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-amber-700 mt-2">
                          Un email avec le lien de téléchargement sera envoyé à {order.delivery_email}
                        </p>
                      </div>
                    )}

                    {order.status === 'delivered' && (
                      <div className="mt-4 p-4 bg-green-50 rounded-lg flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium text-green-800">Livré le {formatDate(order.delivered_at)}</p>
                          {order.download_url && (
                            <a href={order.download_url} target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 hover:underline">
                              {order.download_url}
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
